import { getQueryKey } from "@trpc/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { trpc, trpcVanilla } from "@client/trpc";
import { type LeadClipHintScope } from "@client/offline/offlineCache";
import {
  recommenderVideoMediaInput,
  workspaceRecommenderClipsInputForPath,
} from "@lib/funderWorkspaceRoutes";
import { beginIdentityScopedPrefetch } from "./prefetchIdentityGuard";
import {
  hintViewerUserId,
  initialClipVideoId,
  videoMediaQueryOptions,
  warmHintedLeadClipMedia,
} from "./prefetchWorkspaceProposalClips";

// On a recommender deep link, the URL-derived clips and signed-media requests
// start together. The media query resolves the lead video server-side; a
// query-string target, which pathname cannot identify, keeps the direct path.
//
// Mirrors prefetchWorkspaceProposalClips exactly: the server resolves the
// recommender id to its member id, so nothing has to be read client-side
// before the request can go out.
export function prefetchWorkspaceRecommenderClips(
  queryClient: QueryClient,
  roundId: string,
  pathname: string,
  targetExcerptVideoId: string | null = null,
): void {
  const input = workspaceRecommenderClipsInputForPath(roundId, pathname);
  if (input == null) return;
  const initialMediaInput = recommenderVideoMediaInput(
    roundId,
    input.recommenderId,
  );

  void (async () => {
    const guard = beginIdentityScopedPrefetch(queryClient);
    try {
      // The hint is keyed per effective viewer and read without touching the
      // network. Its scope id is the clips query's own scope — the recommender
      // id — matching what RecommenderTranscriptClipsPanel records.
      const viewerUserId = hintViewerUserId();
      const hintScope: LeadClipHintScope | null =
        viewerUserId == null
          ? null
          : {
              roundId,
              scopeId: input.recommenderId,
              effectiveViewerUserId: viewerUserId,
            };
      const hintedVideoId =
        targetExcerptVideoId == null
          ? null
          : warmHintedLeadClipMedia(queryClient, hintScope);
      if (hintedVideoId != null) {
        // Speculative, but still this chain's entry to clean up if the viewer
        // switches while it is in flight.
        guard.warm(videoMediaQueryOptions({ videoId: hintedVideoId }).queryKey);
      } else if (targetExcerptVideoId == null) {
        const mediaOptions = videoMediaQueryOptions(initialMediaInput);
        guard.warm(mediaOptions.queryKey);
        void queryClient.prefetchQuery(mediaOptions);
      }

      const clips = await queryClient.fetchQuery({
        queryKey: guard.warm(
          getQueryKey(trpc.getRecommenderTranscriptClips, input, "query"),
        ),
        queryFn: () => trpcVanilla.getRecommenderTranscriptClips.query(input),
      });
      if (guard.discardIfViewerChanged()) return;
      const videoId =
        targetExcerptVideoId != null &&
        clips.excerpts.some(
          (excerpt) => excerpt.videoId === targetExcerptVideoId,
        )
          ? targetExcerptVideoId
          : initialClipVideoId(clips);
      if (videoId == null) return;

      const mediaOptions = videoMediaQueryOptions(
        targetExcerptVideoId == null ? initialMediaInput : { videoId },
      );
      guard.warm(mediaOptions.queryKey);
      await queryClient.prefetchQuery(mediaOptions);
      guard.discardIfViewerChanged();
    } catch {
      // This is an optimization only. The mounted hooks retain their normal
      // loading and error behavior if either early request fails.
    }
  })();
}
